<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { CoDeptor } from '@/types/CoDeptor'
import SsnInput from '../../Shared/SSNInput.vue'

const props = defineProps<{ disable?: boolean }>()
const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

// ✅ Dynamic rules for co-debtors
const rules = computed(() => ({
  umsokn: {
    coDeptors: Array.isArray(umsokn.value?.coDeptors)
      ? umsokn.value.coDeptors.map(() => ({
          name: { required },
          ssn: { required },
          phone: { required },
          email: { required, email }
        }))
      : []
  }
}))

const v$ = useVuelidate(rules, { umsokn })

// ✅ Initialize + reset validation after load
onLoaded(() => {
  if (!umsokn.value) return
  if (!Array.isArray(umsokn.value.coDeptors) || umsokn.value.coDeptors.length === 0) {
    umsokn.value.coDeptors = [
      {
        name: '',
        ssn: '',
        phone: '',
        email: '',
        moneyLaunderingRiskCoDeptor: null,
        moneyLaunderingRiskWhy: null
      } as CoDeptor
    ]
  }
  nextTick(() => v$.value.$reset())
})

// ✅ Add/remove helpers
function addCoDeptor() {
  umsokn.value?.coDeptors.push({ name: '', ssn: '', phone: '', email: '' })
}

function removeCoDeptor(i: number) {
  umsokn.value?.coDeptors.splice(i, 1)
  if (umsokn.value && umsokn.value.coDeptors.length === 0) {
    umsokn.value.coDeptors = [{ name: '', ssn: '', phone: '', email: '' }]
  }
}

// ✅ Expose validation
defineExpose({
  async validate() {
    if (Array.isArray(umsokn.value.coDeptors)) {
      umsokn.value.coDeptors.forEach((_: CoDeptor, i: number) => {
        v$.value.umsokn.coDeptors[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_nafnabeyting_umsokn_step4_header1') }}
      </div>

      <transition-group name="list">
        <div v-for="(deptor, i) in umsokn.coDeptors" :key="'coDeptor_' + i">
          <div v-if="i > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              class="forms__warning__list__wrapper"
              @click="removeCoDeptor(i)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" />
            </button>
          </div>

          <v-row>
            <!-- Name -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.name"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step4_nafn')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.coDeptors[i]?.name?.$error"
                :error-messages="v$.umsokn.coDeptors[i]?.name?.$dirty
                  ? [
                      v$.umsokn.coDeptors[i]?.name?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- SSN -->
            <v-col cols="12" md="6">
              <SsnInput
                v-model="deptor.ssn"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step4_kennitala')"
                :error="v$.umsokn.coDeptors[i]?.ssn?.$error"
                :error-messages="v$.umsokn.coDeptors[i]?.ssn?.$dirty
                  ? [
                      v$.umsokn.coDeptors[i]?.ssn?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : '',
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Phone -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.phone"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step4_simi')"
                variant="outlined"
                density="comfortable"
                type="number"
                :error="v$.umsokn.coDeptors[i]?.phone?.$error"
                :error-messages="v$.umsokn.coDeptors[i]?.phone?.$dirty
                  ? [
                      v$.umsokn.coDeptors[i]?.phone?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- Email -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.email"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step4_email')"
                variant="outlined"
                type="email"
                density="comfortable"
                :error="v$.umsokn.coDeptors[i]?.email?.$error"
                :error-messages="v$.umsokn.coDeptors[i]?.email?.$dirty
                  ? [
                      v$.umsokn.coDeptors[i]?.email?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : '',
                      v$.umsokn.coDeptors[i]?.email?.email?.$invalid
                        ? t('lan_vedflutning_step3_samskuldari_email_invalid')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
         <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addCoDeptor"
        >
          {{ $t('lan_nafnabeyting_umsokn_step4_add') }}
        </v-btn>
      </div>

      <v-row style="margin-top: 40px;">
        <v-col>
          <p>{{ t('lan_nafnabeyting_umsokn_step4_disclaimer') }}</p>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__header {
  margin-bottom: 16px;
}
</style>
